import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ITEM_BULK_RESOURCE, isFieldVisible, masterFormSections, masterListColumns, masterListFilters, type MasterColumnDef, type MasterDef } from '@project/contracts'
import { Button, Empty, Icon, MenuButton, Modal, Money, Notice, Panel, Table, toast, type ColumnDef } from '../design/index.js'
import { TeamBoard } from './TeamBoard.js'
import { useApi } from '../app/api.js'
import { Link } from '../app/Link.js'
import { navigate, paths, type Query } from '../app/router.js'
import { FormField } from './controls.js'
import { ErrorBlock, Loading } from './feedback.js'
import { ColumnsButton, ExportButton, useChosenColumns } from './columns.js'
import { asText, formatDate, formatQuantity, formatRate } from './format.js'
import { SavedViews } from './SavedViews.js'
import { FilterFields, type FilterValues } from './filter-fields.js'
import { StarToggle } from './favourites.js'
import { DataImports } from '../pages/DataPages.js'

const useMasterDef = (route: string) => {
  const api = useApi()
  return useQuery({
    queryKey: ['masterDefs'],
    queryFn: () => api.masterMenu(),
    staleTime: 10 * 60_000,
    select: (all) => all.find((d) => d.route === route),
  })
}

const cell = (col: MasterColumnDef, value: unknown) => {
  if (value === null || value === undefined || value === '') return null
  if (col.type === 'boolean') return value === true ? 'Yes' : 'No'
  if (col.type === 'money') return <Money value={String(value)} />
  if (col.type === 'rate') return <span className="num">{formatRate(String(value))}</span>
  if (col.type === 'qty') return <span className="num">{formatQuantity(String(value))}</span>
  if (col.type === 'code') return <span className="mono">{String(value)}</span>
  if (col.type === 'date') return formatDate(String(value).slice(0, 10))
  return asText(value)
}

const MASTER_PAGE = 50

/** The list of one master. Columns, search and paging all come from the def. */
export const MasterListPage = ({ companyId, masterRoute, query }: {
  companyId: string; masterRoute: string; query: Query
}) => {
  const api = useApi()
  const defQ = useMasterDef(masterRoute)
  const def = defQ.data
  const [q, setQ] = useState(query['q'] ?? '')
  const [includeRetired, setIncludeRetired] = useState(false)
  /*
    The setup list as the reference has it (SET-030): a filter panel from the
    master's own choices and references, saved views, Created on / by among
    the columns, and pages rather than the first fifty.
  */
  const filterDefs = useMemo(() => (def ? masterListFilters(def) : []), [def])
  const allColumns = useMemo(() => (def ? masterListColumns(def) : []), [def])
  const [showFilters, setShowFilters] = useState(false)
  const [draft, setDraft] = useState<FilterValues>({})
  const [applied, setApplied] = useState<FilterValues>({})
  const [page, setPage] = useState(1)
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false)

  const filterParams = useMemo(() => {
    const out: Record<string, string> = {}
    for (const [k, v] of Object.entries(applied)) if (v) out[k] = v
    return out
  }, [applied])
  const params = useMemo(
    () => ({
      page: String(page), pageSize: String(MASTER_PAGE), ...filterParams,
      ...(q ? { q } : {}), ...(includeRetired ? { includeRetired: 'true' } : {}),
    }),
    [q, includeRetired, filterParams, page],
  )
  const chooser = useChosenColumns(def ? `master:${def.key}` : '', allColumns, def?.columns ?? [])
  const list = useQuery({
    queryKey: ['masterList', api.companyId, def?.key, params],
    queryFn: () => api.masterList(def?.key ?? '', params),
    enabled: Boolean(def?.key),
    placeholderData: (prev) => prev,
  })

  if (defQ.isLoading) return <div className="page"><Loading /></div>
  if (!def) return <div className="page"><ErrorBlock error={new Error(`No master at '${masterRoute}'`)} /></div>

  const columns: ColumnDef<Record<string, unknown>>[] = [
    ...chooser.visible.map((c) => ({
      key: c.key,
      header: c.label,
      width: c.width,
      align: c.align,
      render: (row: Record<string, unknown>) => cell(c, row[c.key]),
    })),
    {
      key: 'active', header: '', width: 120,
      render: (row: Record<string, unknown>) =>
        row['active'] === false ? <span style={{ color: 'var(--muted)' }}>Inactive</span> : null,
    },
  ]

  return (
    <div className="page">
      <h1 className="page-title">{def.labelPlural}</h1>
      <p className="page-subtitle">{def.blurb}</p>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', margin: '20px 0' }}>
        {/*
          Every master can be loaded from a spreadsheet as well as added one at
          a time. Items keep their own wording and their duplicate-reviewing
          bulk upload; the rest open the master import wizard on themselves.
        */}
        <MenuButton label={`Add ${def.label.toLowerCase()}`} kind="primary" icon="plus" showChevron align="left"
          items={[
            { key: 'single', label: def.key === 'item' ? 'Add single item' : `Add ${def.label.toLowerCase()}`, icon: 'plus',
              onSelect: () => navigate(paths.masterRecord(companyId, masterRoute, 'new')) },
            { key: 'bulk', label: 'Bulk upload', icon: 'layers', onSelect: () => setBulkUploadOpen(true) },
          ]} />
        <input
          className="control" style={{ maxWidth: 320 }} value={q}
          placeholder={`Search ${def.labelPlural.toLowerCase()}`}
          onChange={(e) => { setQ(e.currentTarget.value); setPage(1) }}
        />
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 44, font: '500 14px/1 var(--font-body)' }}>
          <input type="checkbox" checked={includeRetired} onChange={(e) => { setIncludeRetired(e.currentTarget.checked); setPage(1) }} />
          Show inactive
        </label>
        {filterDefs.length > 0 ? (
          <Button kind="ghost" icon={<Icon name="filter" size={18} />} aria-expanded={showFilters}
            onClick={() => setShowFilters((v) => !v)}>
            {showFilters ? 'Hide filters' : 'Show filters'}
          </Button>
        ) : null}
        <SavedViews
          scope={`master:${def.key}`}
          filters={{ ...filterParams, ...(q ? { q } : {}), ...(includeRetired ? { includeRetired: 'true' } : {}) }}
          columns={chooser.chosen}
          editor={{
            columns: allColumns,
            currentColumns: chooser.visible.map((c) => c.key),
            filters: filterDefs,
            currentFilters: applied,
            toParams: (values) => Object.fromEntries(Object.entries(values).filter(([, v]) => v !== '')),
            fromParams: (stored) => Object.fromEntries(Object.entries(stored).filter(([k]) => k !== 'q' && k !== 'includeRetired')),
          }}
          onApply={(view) => {
            const values = Object.fromEntries(Object.entries(view.filters).filter(([k]) => k !== 'q' && k !== 'includeRetired'))
            setDraft(values)
            setApplied(values)
            setQ(view.filters['q'] ?? '')
            setIncludeRetired(view.filters['includeRetired'] === 'true')
            setPage(1)
            if (view.columns.length > 0) chooser.choose(view.columns)
          }}
          onDefault={(view) => {
            if (Object.keys(query).length > 0) return
            const values = Object.fromEntries(Object.entries(view.filters).filter(([k]) => k !== 'q' && k !== 'includeRetired'))
            setDraft(values)
            setApplied(values)
            if (view.columns.length > 0) chooser.choose(view.columns)
          }}
        />
        <ColumnsButton all={allColumns} state={chooser} title={`Columns on ${def.labelPlural.toLowerCase()}`} />
        <ExportButton
          href={api.exportUrl('masters', def.key, params, chooser.chosen)}
          disabled={(list.data?.total ?? 0) === 0}
          count={list.data?.total ?? 0}
          what={def.labelPlural.toLowerCase()}
        />
        <span className="num" style={{ marginLeft: 'auto', color: 'var(--muted)' }}>
          {list.data ? `${list.data.total} record${list.data.total === 1 ? '' : 's'}` : ''}
        </span>
      </div>

      {list.error ? <ErrorBlock error={list.error} /> : null}
      <div className="list-layout">
      <div className="list-layout__main">
      {list.data && list.data.rows.length === 0 ? (
        <Empty
          title={`No ${def.labelPlural.toLowerCase()} yet`}
          body={q ? 'Nothing matches that search. Clear it to see everything.' : def.blurb}
          actions={<Button kind="primary" onClick={() => navigate(paths.masterRecord(companyId, masterRoute, 'new'))}>
            Add {def.label.toLowerCase()}
          </Button>}
        />
      ) : (
        <>
          <Table
            columns={columns}
            rows={[...(list.data?.rows ?? [])]}
            getKey={(r) => String(r['id'])}
            onRowClick={(r) => navigate(paths.masterRecord(companyId, masterRoute, String(r['id'])))}
          />
          {(list.data?.total ?? 0) > MASTER_PAGE ? (
            <div className="pager">
              <Button kind="ghost" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Previous</Button>
              <span className="list-count">
                Page {page} of {Math.max(1, Math.ceil((list.data?.total ?? 0) / MASTER_PAGE))}
              </span>
              <Button kind="ghost" disabled={page >= Math.ceil((list.data?.total ?? 0) / MASTER_PAGE)}
                onClick={() => setPage((p) => p + 1)}>Next</Button>
            </div>
          ) : null}
        </>
      )}
      </div>
      {showFilters ? (
        <aside className="filter-drawer" aria-label="Filters">
          <h2 className="filter-drawer__title">Filters</h2>
          <p className="helper">Narrow the list, then apply.</p>
          <FilterFields filters={filterDefs} values={draft}
            onChange={(key, value) => setDraft((prev) => ({ ...prev, [key]: value }))} />
          <div className="row" style={{ marginTop: 24 }}>
            <Button kind="primary" onClick={() => { setApplied(draft); setPage(1) }}>Apply</Button>
            <Button kind="ghost" onClick={() => { setDraft({}); setApplied({}); setPage(1) }}>Clear</Button>
          </div>
        </aside>
      ) : null}
      </div>
      {bulkUploadOpen ? <Modal title={`Bulk upload ${def.labelPlural.toLowerCase()}`} wide
        subtitle={def.key === 'item'
          ? 'Upload a spreadsheet, review possible duplicates, then import.'
          : 'Download the spreadsheet, fill it in or use an export from the old system, check it, then import.'}
        onClose={() => setBulkUploadOpen(false)}
        actions={<Button kind="ghost" onClick={() => setBulkUploadOpen(false)}>Close</Button>}>
        <DataImports fixedResource={def.key === 'item' ? ITEM_BULK_RESOURCE : def.key} />
      </Modal> : null}
    </div>
  )
}

/**
 * Add or edit one record. The form is the def's field list, nothing more.
 *
 * Laid out as the reference lays out every setup record (SET1-009): the
 * breadcrumb "<Type> › <name>" with an Active chip, a Form tab, Close and
 * Action ▾ (Save, Deactivate/Activate, Delete) in the header — Close and
 * Create on a new one — the fields in titled cards that start with "General
 * Info" (`masterFormSections`, HOME-014), and the Team Board on the right.
 */
export const MasterRecordPage = ({ companyId, masterRoute, id }: {
  companyId: string; masterRoute: string; id: string
}) => {
  const api = useApi()
  const client = useQueryClient()
  const defQ = useMasterDef(masterRoute)
  const def = defQ.data
  const isNew = id === 'new'

  const record = useQuery({
    queryKey: ['masterRecord', api.companyId, def?.key, id],
    queryFn: () => api.masterRecord(def?.key ?? '', id),
    enabled: Boolean(def?.key) && !isNew,
  })

  const [draft, setDraft] = useState<Record<string, unknown> | null>(null)
  // A new record starts from each field's default — a tax starts as GST.
  const defaults = isNew
    ? Object.fromEntries((def?.fields ?? []).filter((f) => f.defaultValue !== undefined).map((f) => [f.key, f.defaultValue]))
    : {}
  const values = draft ?? record.data ?? defaults
  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  const save = useMutation({
    mutationFn: (fields: Record<string, unknown>) =>
      isNew ? api.masterCreate(def?.key ?? '', fields) : api.masterUpdate(def?.key ?? '', id, fields),
    onSuccess: (saved) => {
      void client.invalidateQueries({ queryKey: ['masterList'] })
      void client.invalidateQueries({ queryKey: ['masterRecord'] })
      void client.invalidateQueries({ queryKey: ['lookup'] })
      toast('The master has been saved successfully!')
      navigate(paths.masterRecord(companyId, masterRoute, String(saved['id'])))
      setDraft(null); setError(null); setFieldErrors({})
    },
    onError: (err: unknown) => {
      const details = (err as { details?: { fields?: Record<string, string> } })?.details
      setFieldErrors(details?.fields ?? {})
      setError(details?.fields ? null : err instanceof Error ? err.message : 'Could not save.')
    },
  })

  const retire = useMutation({
    mutationFn: (active: boolean) => api.masterSetActive(def?.key ?? '', id, active),
    onSuccess: (_saved, active) => {
      void client.invalidateQueries({ queryKey: ['masterRecord'] }); void client.invalidateQueries({ queryKey: ['masterList'] })
      toast(active ? 'The master has been activated.' : 'The master has been deactivated.')
    },
    onError: (err: unknown) => setError(err instanceof Error ? err.message : 'Could not change it.'),
  })

  // Delete, for a record nothing uses (SET-031). The server says why not.
  const [confirmDelete, setConfirmDelete] = useState(false)
  const remove = useMutation({
    mutationFn: () => api.masterDelete(def?.key ?? '', id),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['masterList'] })
      void client.invalidateQueries({ queryKey: ['lookup'] })
      toast('The master has been deleted successfully!')
      navigate(paths.masterList(companyId, masterRoute))
    },
    onError: (err: unknown) => {
      setConfirmDelete(false)
      setError(err instanceof Error ? err.message : 'Could not delete it.')
    },
  })

  if (defQ.isLoading || (!isNew && record.isLoading)) return <div className="page"><Loading /></div>
  if (!def) return <div className="page"><ErrorBlock error={new Error(`No master at '${masterRoute}'`)} /></div>
  if (record.error) return <div className="page"><ErrorBlock error={record.error} /></div>

  const set = (key: string, value: unknown) => setDraft({ ...values, [key]: value })
  const retired = values['active'] === false
  const titleKey = def.fields.some((f) => f.key === 'name') ? 'name' : def.columns[0]?.key ?? 'name'
  const title = isNew ? `New ${def.label.toLowerCase()}` : asText(values[titleKey]) || asText(values[def.columns[0]?.key ?? 'name']) || def.label
  const cards = masterFormSections(def)

  /*
    Required on the form: the server's own `required`, and `requiredOnForm`
    (an item's Type and Item Category), which only this page insists on.
    Checked here first so the operator is told field by field.
  */
  const submit = () => {
    const missing: Record<string, string> = {}
    for (const field of def.fields) {
      if (field.importOnly || !(field.required || field.requiredOnForm) || !isFieldVisible(field, values)) continue
      // A code the server hands out is not asked for, whatever the field says.
      if (field.key === 'code' && def.autoCode) continue
      const v = values[field.key]
      if (v === undefined || v === null || v === '') missing[field.key] = `${field.label} is required.`
    }
    if (Object.keys(missing).length > 0) {
      setFieldErrors(missing)
      setError(null)
      return
    }
    save.mutate(Object.fromEntries(Object.entries(values).filter(([k]) => !def.fields.some((f) => f.key === k && f.importOnly))))
  }

  const close = () => navigate(paths.masterList(companyId, masterRoute))
  const actions = [
    { key: 'save', label: save.isPending ? 'Saving…' : 'Save', onSelect: submit },
    ...(def.softDelete ? [{
      key: 'active', label: retired ? 'Activate' : 'Deactivate', onSelect: () => retire.mutate(retired),
    }] : []),
    { key: 'delete', label: 'Delete', danger: true, onSelect: () => setConfirmDelete(true) },
  ]

  return (
    <div className="page">
      <div className="record-head">
        <div className="record-head__title">
          <p className="crumb" style={{ margin: 0 }}>
            <Link to={paths.masterList(companyId, masterRoute)}>{def.label}</Link>
            <Icon name="right" size={15} />
          </p>
          <h1 className="page-title" style={{ margin: 0 }}>{title}</h1>
          {!isNew && def.softDelete ? (
            <span className={retired ? 'chip chip--muted' : 'chip chip--good'}>{retired ? 'Inactive' : 'Active'}</span>
          ) : null}
        </div>
        <div className="record-head__actions">
          <Button kind="ghost" onClick={close}>Close</Button>
          {isNew ? (
            <Button kind="primary" disabled={save.isPending} onClick={submit}>
              {save.isPending ? 'Creating…' : 'Create'}
            </Button>
          ) : (
            <MenuButton label="Action" kind="primary" icon="dots" showChevron
              disabled={save.isPending || retire.isPending || remove.isPending} items={actions} />
          )}
        </div>
      </div>

      <nav className="tabstrip" aria-label="Record sections">
        <button type="button" className="tabstrip__tab tabstrip__tab--active" aria-current="page">Form</button>
      </nav>

      {retired ? (
        <div style={{ margin: '16px 0' }}>
          <Notice kind="warn">
            This {def.label.toLowerCase()} is inactive. It stays on documents that already use it, but cannot be chosen on new ones.
          </Notice>
        </div>
      ) : null}
      {error ? <div style={{ margin: '16px 0' }}><Notice kind="bad">{error}</Notice></div> : null}

      <div className="record-layout">
        <div className="record-layout__main">
          {cards.map((card) => (
            <Panel key={card.title} title={card.title}>
              <div className="field-grid">
                {card.fields.filter((field) => isFieldVisible(field, values)).map((field) => (
                  <FormField
                    key={field.key}
                    field={field.requiredOnForm ? { ...field, required: true } : field}
                    value={(values[field.key] ?? '') as string}
                    onChange={(v) => set(field.key, v)}
                    scopeValues={values}
                    // Fixed once the record exists — an item's Type and Item Category.
                    disabled={field.lockAfterSave === true && !isNew}
                    error={fieldErrors[field.key]}
                  />
                ))}
              </div>
            </Panel>
          ))}
        </div>
        {/* Notes and files on the record, and what changed it (SET-031). */}
        {!isNew ? (
          <aside className="record-layout__aside">
            <TeamBoard record={{ type: def.key, id }} />
          </aside>
        ) : null}
      </div>

      {confirmDelete ? (
        <Modal
          title="Deleting master?"
          tone="warn"
          onClose={() => setConfirmDelete(false)}
          actions={
            <>
              <Button kind="ghost" onClick={() => setConfirmDelete(false)}>No, don’t delete it</Button>
              <Button kind="primary" disabled={remove.isPending} onClick={() => remove.mutate()}>Yes, delete it</Button>
            </>
          }
        >
          <p>Deleted masters are permanently removed. Anything that uses this {def.label.toLowerCase()} stops it being deleted — deactivate it instead.</p>
        </Modal>
      ) : null}
    </div>
  )
}

/** The Masters menu, grouped the way the registry groups them. */
export const MastersHub = ({ companyId }: { companyId: string }) => {
  const api = useApi()
  const menu = useQuery({ queryKey: ['masterDefs'], queryFn: () => api.masterMenu(), staleTime: 10 * 60_000 })

  const groups = useMemo(() => {
    const byGroup = new Map<string, MasterDef[]>()
    for (const d of menu.data ?? []) {
      const list = byGroup.get(d.group)
      if (list) list.push(d)
      else byGroup.set(d.group, [d])
    }
    return [...byGroup.entries()]
  }, [menu.data])

  if (menu.isLoading) return <div className="page"><Loading /></div>
  if (menu.error) return <div className="page"><ErrorBlock error={menu.error} /></div>

  return (
    <div className="page">
      <h1 className="page-title">Masters</h1>
      <p className="page-subtitle">The reference data every document is built from.</p>
      {groups.map(([group, items]) => (
        <Panel key={group} title={group} aside={`${items.length} master${items.length === 1 ? '' : 's'}`}>
          <div className="tile-grid">
            {items.map((d) => (
              <div className="tile" key={d.key}>
                <StarToggle menuKey={`master:${d.key}`} label={d.labelPlural} />
                <div className="tile__body">
                  <Link to={paths.masterList(companyId, d.route)} className="tile__title">{d.labelPlural}</Link>
                  <p className="tile__desc">{d.blurb}</p>
                </div>
              </div>
            ))}
          </div>
        </Panel>
      ))}
    </div>
  )
}
